/**
 * GlobalView — Aircraft Icons by Category
 *
 * SVG icons for all 21 OpenSky ADS-B emitter categories (0–20).
 * Each SVG is oriented pointing upward (north / 0° heading).
 *
 * Provides two rendering modes:
 * - Data URI (for Cesium Billboards): getAircraftIconDataUri()
 * - Canvas (for Cesium PlaneGraphics materials): getAircraftIconCanvas()
 *
 * Source: SVG_Symbole.html
 */

// ─── Category Labels ─────────────────────────────────────────────

const CATEGORY_LABELS: Record<number, string> = {
  0: 'No information',
  1: 'No ADS-B Emitter Cat.',
  2: 'Light (< 15500 lbs)',
  3: 'Small',
  4: 'Large',
  5: 'High Vortex Large',
  6: 'Heavy',
  7: 'High Performance',
  8: 'Rotorcraft',
  9: 'Glider / Sailplane',
  10: 'Lighter-than-air',
  11: 'Parachutist',
  12: 'Ultralight',
  13: 'Reserved',
  14: 'UAV',
  15: 'Space',
  16: 'Emergency Vehicle',
  17: 'Service Vehicle',
  18: 'Point Obstacle',
  19: 'Cluster Obstacle',
  20: 'Line Obstacle',
};

/** Get human-readable category label */
export function getCategoryLabel(category: number): string {
  return CATEGORY_LABELS[category] ?? `Unknown (${category})`;
}

// ─── SVG Path Data per Category ──────────────────────────────────

/**
 * Main path data for each category (viewBox 0 0 24 24).
 * Used for both data URI and Canvas rendering.
 */
const MAIN_PATHS: Record<number, string> = {
  1: 'M12 2 C11 2 10 3 10 5 L10 10 L3 14 L3 16 L10 14 L10 19 L7 21 L7 22 L12 21 L17 22 L17 21 L14 19 L14 14 L21 16 L21 14 L14 10 L14 5 C14 3 13 2 12 2 Z',
  2: 'M12 3 C11 3 10.5 4 10.5 5 L10.5 10 L2 10 L2 12 L10.5 12 L10.5 19 L8 20 L8 21 L12 20.5 L16 21 L16 20 L13.5 19 L13.5 12 L22 12 L22 10 L13.5 10 L13.5 5 C13.5 4 13 3 12 3 Z',
  3: 'M12 2.5 C11 2.5 10 3.5 10 5 L10 10 L4 13 L4 15 L10 13.5 L10 19 L7.5 21 L7.5 22 L12 21.5 L16.5 22 L16.5 21 L14 19 L14 13.5 L20 15 L20 13 L14 10 L14 5 C14 3.5 13 2.5 12 2.5 Z',
  4: 'M12 1 C10.5 1 9.5 2.5 9.5 5 L9.5 10 L2 15 L2 17 L9.5 14.5 L9.5 20 L6 22 L6 23 L12 22 L18 23 L18 22 L14.5 20 L14.5 14.5 L22 17 L22 15 L14.5 10 L14.5 5 C14.5 2.5 13.5 1 12 1 Z',
  5: 'M12 1 C10.5 1 9.5 2.5 9.5 5 L9.5 10 L2 15 L2 17 L9.5 14.5 L9.5 20 L6 22 L6 23 L12 22 L18 23 L18 22 L14.5 20 L14.5 14.5 L22 17 L22 15 L14.5 10 L14.5 5 C14.5 2.5 13.5 1 12 1 Z',
  6: 'M12 1 C10 1 9 3 9 6 L9 11 L1 16 L1 18 L9 15 L9 20 L5 23 L5 24 L12 23 L19 24 L19 23 L15 20 L15 15 L23 18 L23 16 L15 11 L15 6 C15 3 14 1 12 1 Z',
  7: 'M12 2 L10 8 L3 16 L3 18 L10 16 L10 20 L8 22 L8 23 L12 22 L16 23 L16 22 L14 20 L14 16 L21 18 L21 16 L14 8 Z',
  8: 'M12 6 C9 6 8 8 8 11 C8 14 10 15 10 15 L10 20 L9 20 L9 21 L15 21 L15 20 L14 20 L14 15 C14 15 16 14 16 11 C16 8 15 6 12 6 Z',
  9: 'M12 3 C11.5 3 11 4 11 6 L11 11 L0.5 11 L0.5 12.5 L11 12.5 L11 18 L9 19 L9 20 L12 19.5 L15 20 L15 19 L13 18 L13 12.5 L23.5 12.5 L23.5 11 L13 11 L13 6 C13 4 12.5 3 12 3 Z',
  12: 'M12 4 L2 14 L12 11 L22 14 Z',
  15: 'M12 2 C10 4 9 8 9 13 L5 19 L5 21 L9 19 L9 22 L12 21 L15 22 L15 19 L19 21 L19 19 L15 13 C15 8 14 4 12 2 Z',
};

// ─── Canvas Rendering ────────────────────────────────────────────

const CANVAS_SIZE = 64;
const SVG_VIEWBOX = 24;

/** Canvas cache: key = 'category:fill' → Canvas */
const canvasCache = new Map<string, HTMLCanvasElement>();

/**
 * Setup canvas context with common SVG styling and scaling.
 */
function setupCtx(
  ctx: CanvasRenderingContext2D,
  fill: string,
): void {
  const scale = CANVAS_SIZE / SVG_VIEWBOX;
  ctx.scale(scale, scale);
  ctx.fillStyle = fill;
  ctx.strokeStyle = 'black';
  ctx.lineWidth = 1;
  ctx.lineJoin = 'round';
  ctx.lineCap = 'round';
}

/**
 * Draw a main path shape on the context.
 */
function drawMainPath(
  ctx: CanvasRenderingContext2D,
  pathData: string,
): void {
  const path = new Path2D(pathData);
  ctx.fill(path);
  ctx.stroke(path);
}

/**
 * Category-specific Canvas drawing functions.
 */
function drawCat0(ctx: CanvasRenderingContext2D, fill: string): void {
  // Generic aircraft silhouette (same as category 1) for unknown types
  drawCatPath(ctx, fill, 1);
}

function drawCatPath(ctx: CanvasRenderingContext2D, fill: string, category: number): void {
  setupCtx(ctx, fill);
  const pathData = MAIN_PATHS[category] ?? MAIN_PATHS[1];
  drawMainPath(ctx, pathData);

  // Additional elements per category
  if (category === 4) {
    // Engine pods
    ctx.fillRect(6, 13.5, 1.5, 3);
    ctx.strokeRect(6, 13.5, 1.5, 3);
    ctx.fillRect(16.5, 13.5, 1.5, 3);
    ctx.strokeRect(16.5, 13.5, 1.5, 3);
  } else if (category === 5) {
    // Vortex marks
    ctx.fillStyle = 'none';
    const v1 = new Path2D('M4 19 C3 19 2 18 2 17 C2 16 3 15 4 15 C5 15 5 16 5 16');
    const v2 = new Path2D('M20 19 C21 19 22 18 22 17 C22 16 21 15 20 15 C19 15 19 16 19 16');
    ctx.stroke(v1);
    ctx.stroke(v2);
  } else if (category === 6) {
    // Four engines
    ctx.fillRect(3.5, 15, 1.5, 2.5);
    ctx.strokeRect(3.5, 15, 1.5, 2.5);
    ctx.fillRect(6.5, 14, 1.5, 2.5);
    ctx.strokeRect(6.5, 14, 1.5, 2.5);
    ctx.fillRect(14, 14, 1.5, 2.5);
    ctx.strokeRect(14, 14, 1.5, 2.5);
    ctx.fillRect(19, 15, 1.5, 2.5);
    ctx.strokeRect(19, 15, 1.5, 2.5);
  } else if (category === 8) {
    // Rotor cross
    ctx.lineWidth = 0.8;
    ctx.beginPath();
    ctx.moveTo(12, 3); ctx.lineTo(12, 21);
    ctx.moveTo(2, 11); ctx.lineTo(22, 11);
    ctx.stroke();
  } else if (category === 12) {
    // Pilot circle and line
    ctx.beginPath();
    ctx.arc(12, 17, 1.5, 0, Math.PI * 2);
    ctx.fill();
    ctx.stroke();
    ctx.beginPath();
    ctx.moveTo(12, 11);
    ctx.lineTo(12, 17);
    ctx.stroke();
  }
}

function drawCat10(ctx: CanvasRenderingContext2D, fill: string): void {
  setupCtx(ctx, fill);
  // Blimp body
  ctx.beginPath();
  ctx.ellipse(12, 11, 6, 10, 0, 0, Math.PI * 2);
  ctx.fill();
  ctx.stroke();
  // Bottom fin
  ctx.beginPath();
  ctx.moveTo(10, 21); ctx.lineTo(14, 21); ctx.lineTo(12, 23); ctx.closePath();
  ctx.fill(); ctx.stroke();
  // Side fins
  ctx.beginPath();
  ctx.moveTo(6, 11); ctx.lineTo(4, 13); ctx.lineTo(4, 9); ctx.closePath();
  ctx.fill(); ctx.stroke();
  ctx.beginPath();
  ctx.moveTo(18, 11); ctx.lineTo(20, 13); ctx.lineTo(20, 9); ctx.closePath();
  ctx.fill(); ctx.stroke();
}

function drawCat11(ctx: CanvasRenderingContext2D, fill: string): void {
  setupCtx(ctx, fill);
  // Parachute canopy
  const canopy = new Path2D('M4 11 C4 5 20 5 20 11 L14 15 L10 15 Z');
  ctx.fill(canopy); ctx.stroke(canopy);
  // Pilot
  ctx.beginPath();
  ctx.arc(12, 18, 1.5, 0, Math.PI * 2);
  ctx.fill(); ctx.stroke();
  // Lines
  ctx.beginPath();
  ctx.moveTo(10, 15); ctx.lineTo(12, 18); ctx.lineTo(14, 15);
  ctx.stroke();
}

function drawCat13(ctx: CanvasRenderingContext2D, fill: string): void {
  setupCtx(ctx, fill);
  // Diamond
  ctx.beginPath();
  ctx.moveTo(12, 3); ctx.lineTo(21, 12); ctx.lineTo(12, 21); ctx.lineTo(3, 12); ctx.closePath();
  ctx.fill(); ctx.stroke();
  // R text
  ctx.fillStyle = 'black';
  ctx.font = 'bold 10px sans-serif';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText('R', 12, 12);
}

function drawCat14(ctx: CanvasRenderingContext2D, fill: string): void {
  setupCtx(ctx, fill);
  // Arms
  ctx.beginPath();
  ctx.moveTo(10, 10); ctx.lineTo(6, 6);
  ctx.moveTo(14, 10); ctx.lineTo(18, 6);
  ctx.moveTo(10, 14); ctx.lineTo(6, 18);
  ctx.moveTo(14, 14); ctx.lineTo(18, 18);
  ctx.stroke();
  // Center
  ctx.beginPath(); ctx.arc(12, 12, 3, 0, Math.PI * 2); ctx.fill(); ctx.stroke();
  // Rotors
  ctx.beginPath(); ctx.arc(6, 6, 2.5, 0, Math.PI * 2); ctx.fill(); ctx.stroke();
  ctx.beginPath(); ctx.arc(18, 6, 2.5, 0, Math.PI * 2); ctx.fill(); ctx.stroke();
  ctx.beginPath(); ctx.arc(6, 18, 2.5, 0, Math.PI * 2); ctx.fill(); ctx.stroke();
  ctx.beginPath(); ctx.arc(18, 18, 2.5, 0, Math.PI * 2); ctx.fill(); ctx.stroke();
}

function drawCat16(ctx: CanvasRenderingContext2D, fill: string): void {
  setupCtx(ctx, fill);
  // Vehicle body
  ctx.beginPath();
  ctx.roundRect(7, 3, 10, 18, 2);
  ctx.fill(); ctx.stroke();
  // Cross
  ctx.fillStyle = 'white';
  const cross = new Path2D('M11 9 H13 V11 H15 V13 H13 V15 H11 V13 H9 V11 H11 Z');
  ctx.fill(cross);
}

function drawCat17(ctx: CanvasRenderingContext2D, fill: string): void {
  setupCtx(ctx, fill);
  // Vehicle body
  ctx.beginPath();
  ctx.roundRect(7, 3, 10, 18, 2);
  ctx.fill(); ctx.stroke();
  // Divider line
  ctx.beginPath();
  ctx.moveTo(7, 8); ctx.lineTo(17, 8);
  ctx.stroke();
}

function drawCat18(ctx: CanvasRenderingContext2D, fill: string): void {
  setupCtx(ctx, fill);
  ctx.beginPath(); ctx.arc(12, 12, 8, 0, Math.PI * 2); ctx.fill(); ctx.stroke();
  ctx.fillStyle = 'black';
  ctx.beginPath(); ctx.arc(12, 12, 2, 0, Math.PI * 2); ctx.fill();
}

function drawCat19(ctx: CanvasRenderingContext2D, fill: string): void {
  setupCtx(ctx, fill);
  // Three circles with dots
  const positions = [[12, 6, 4], [6, 16, 4], [18, 16, 4]] as const;
  for (const [cx, cy, r] of positions) {
    ctx.beginPath(); ctx.arc(cx, cy, r, 0, Math.PI * 2); ctx.fill(); ctx.stroke();
    ctx.fillStyle = 'black';
    ctx.beginPath(); ctx.arc(cx, cy, 1, 0, Math.PI * 2); ctx.fill();
    ctx.fillStyle = fill;
  }
}

function drawCat20(ctx: CanvasRenderingContext2D, fill: string): void {
  setupCtx(ctx, fill);
  // Two circles
  ctx.beginPath(); ctx.arc(5, 5, 3, 0, Math.PI * 2); ctx.fill(); ctx.stroke();
  ctx.beginPath(); ctx.arc(19, 19, 3, 0, Math.PI * 2); ctx.fill(); ctx.stroke();
  // Dashed line
  ctx.setLineDash([2, 2]);
  ctx.beginPath(); ctx.moveTo(7, 7); ctx.lineTo(17, 17); ctx.stroke();
  ctx.setLineDash([]);
  // Dots
  ctx.fillStyle = 'black';
  ctx.beginPath(); ctx.arc(5, 5, 1, 0, Math.PI * 2); ctx.fill();
  ctx.beginPath(); ctx.arc(19, 19, 1, 0, Math.PI * 2); ctx.fill();
}

/** Drawing dispatch map */
const DRAW_FUNCTIONS: Record<number, (ctx: CanvasRenderingContext2D, fill: string) => void> = {
  0: drawCat0,
  10: drawCat10,
  11: drawCat11,
  13: drawCat13,
  14: drawCat14,
  16: drawCat16,
  17: drawCat17,
  18: drawCat18,
  19: drawCat19,
  20: drawCat20,
};

/**
 * Get a Canvas element with the aircraft icon drawn on it.
 * Results are cached for performance. Suitable for Cesium PlaneGraphics material.
 *
 * @param category OpenSky emitter category (0–20)
 * @param fill CSS color for the icon fill
 * @returns HTMLCanvasElement with the icon drawn
 */
export function getAircraftIconCanvas(category: number, fill = '#00d4ff'): HTMLCanvasElement {
  const key = `${category}:${fill}`;
  const cached = canvasCache.get(key);
  if (cached) return cached;

  const canvas = document.createElement('canvas');
  canvas.width = CANVAS_SIZE;
  canvas.height = CANVAS_SIZE;
  const ctx = canvas.getContext('2d');
  if (!ctx) return canvas;

  // Check if there's a specific drawing function
  const drawFn = DRAW_FUNCTIONS[category];
  if (drawFn) {
    drawFn(ctx, fill);
  } else {
    // Default: use path data (covers categories 1-9, 12, 15)
    drawCatPath(ctx, fill, category);
  }

  canvasCache.set(key, canvas);
  return canvas;
}

// ─── Data URI (for Billboards fallback) ──────────────────────────

/**
 * SVG generator functions for data URI mode.
 */
function makeSvg(innerSvg: string, fill: string): string {
  return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="32" height="32"><g fill="${fill}" stroke="black" stroke-width="1" stroke-linejoin="round" stroke-linecap="round">${innerSvg}</g></svg>`;
}

const SVG_INNER: Record<number, (fill: string) => string> = {
  0: () => `<path d="${MAIN_PATHS[1]}"/>`,  // Generic aircraft silhouette for unknown types
  1: () => `<path d="${MAIN_PATHS[1]}"/>`,
  2: () => `<path d="${MAIN_PATHS[2]}"/>`,
  3: () => `<path d="${MAIN_PATHS[3]}"/>`,
  4: () => `<path d="${MAIN_PATHS[4]}"/><rect x="6" y="13.5" width="1.5" height="3" rx="0.5"/><rect x="16.5" y="13.5" width="1.5" height="3" rx="0.5"/>`,
  5: () => `<path d="${MAIN_PATHS[5]}"/><path d="M4 19 C3 19 2 18 2 17 C2 16 3 15 4 15 C5 15 5 16 5 16" fill="none"/><path d="M20 19 C21 19 22 18 22 17 C22 16 21 15 20 15 C19 15 19 16 19 16" fill="none"/>`,
  6: () => `<path d="${MAIN_PATHS[6]}"/><rect x="3.5" y="15" width="1.5" height="2.5" rx="0.5"/><rect x="6.5" y="14" width="1.5" height="2.5" rx="0.5"/><rect x="14" y="14" width="1.5" height="2.5" rx="0.5"/><rect x="19" y="15" width="1.5" height="2.5" rx="0.5"/>`,
  7: () => `<path d="${MAIN_PATHS[7]}"/>`,
  8: () => `<path d="${MAIN_PATHS[8]}"/><path d="M12 3 L12 21 M2 11 L22 11" fill="none" stroke-width="0.8"/>`,
  9: () => `<path d="${MAIN_PATHS[9]}"/>`,
  10: () => '<ellipse cx="12" cy="11" rx="6" ry="10"/><path d="M10 21 L14 21 L12 23 Z"/><path d="M6 11 L4 13 L4 9 Z"/><path d="M18 11 L20 13 L20 9 Z"/>',
  11: () => '<path d="M4 11 C4 5 20 5 20 11 L14 15 L10 15 Z"/><circle cx="12" cy="18" r="1.5"/><path d="M10 15 L12 18 L14 15" fill="none"/>',
  12: () => `<path d="${MAIN_PATHS[12]}"/><circle cx="12" cy="17" r="1.5"/><path d="M12 11 L12 17" fill="none"/>`,
  13: () => '<polygon points="12 3, 21 12, 12 21, 3 12"/><text x="12" y="15.5" font-size="10" text-anchor="middle" font-weight="bold" fill="black" stroke="none">R</text>',
  14: () => '<path d="M10 10 L6 6 M14 10 L18 6 M10 14 L6 18 M14 14 L18 18" fill="none"/><circle cx="12" cy="12" r="3"/><circle cx="6" cy="6" r="2.5"/><circle cx="18" cy="6" r="2.5"/><circle cx="6" cy="18" r="2.5"/><circle cx="18" cy="18" r="2.5"/>',
  15: () => `<path d="${MAIN_PATHS[15]}"/>`,
  16: () => '<rect x="7" y="3" width="10" height="18" rx="2"/><path d="M11 9 H13 V11 H15 V13 H13 V15 H11 V13 H9 V11 H11 Z" fill="white"/>',
  17: () => '<rect x="7" y="3" width="10" height="18" rx="2"/><line x1="7" y1="8" x2="17" y2="8" fill="none"/>',
  18: () => '<circle cx="12" cy="12" r="8"/><circle cx="12" cy="12" r="2" fill="black"/>',
  19: () => '<circle cx="12" cy="6" r="4"/><circle cx="6" cy="16" r="4"/><circle cx="18" cy="16" r="4"/><circle cx="12" cy="6" r="1" fill="black"/><circle cx="6" cy="16" r="1" fill="black"/><circle cx="18" cy="16" r="1" fill="black"/>',
  20: () => '<circle cx="5" cy="5" r="3"/><circle cx="19" cy="19" r="3"/><line x1="7" y1="7" x2="17" y2="17" fill="none" stroke-dasharray="2,2"/><circle cx="5" cy="5" r="1" fill="black"/><circle cx="19" cy="19" r="1" fill="black"/>',
};

const DEFAULT_CATEGORY = 1;
const dataUriCache = new Map<string, string>();

/** Get SVG data URI for billboards (fallback) */
export function getAircraftIconDataUri(category: number, fill = '#00d4ff'): string {
  const key = `${category}:${fill}`;
  const cached = dataUriCache.get(key);
  if (cached) return cached;

  const innerFn = SVG_INNER[category] ?? SVG_INNER[DEFAULT_CATEGORY];
  const svg = makeSvg(innerFn(fill), fill);
  const uri = `data:image/svg+xml;base64,${btoa(svg)}`;

  dataUriCache.set(key, uri);
  return uri;
}

/** Clear all icon caches */
export function clearIconCache(): void {
  dataUriCache.clear();
  canvasCache.clear();
}
